import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  TextField,
  InputAdornment
} from '@mui/material';
import { 
  Droplets, 
  TrendingUp, 
  AlertTriangle, 
  Coins 
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import {
  ComposedChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LabelList
} from 'recharts';

import StatsCard from '../Components/UI/StatsCard';
import FullPageLoader from '../Components/UI/FullPageLoader';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';
import { LakeInfestationMap } from '../Components/Charts';

/**
 * LakeDashboardModern - Unified Lake Management Dashboard
 * 
 * Replaces the fragmented lake dashboard components with a single modern page.
 * All charts, stats, and configuration defined in one place using MUI Grid.
 */
function LakeDashboardModern() {
  const theme = useTheme();
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      total_lakes: 0,
      infested_lakes: 0,
      lakes_at_risk: 0,
      total_area: 0,
      infested_area: 0,
      risk_area: 0
    },
    infestationData: [],
    areaComparison: {
      infested: [],
      total: [],
      lakeNames: []
    },
    thresholdBreaches: [],
    expenseData: [],
    totalExpense: 0
  });
  // Legacy price_predictor was computed at ₹65 per m² of infested area; use
  // that as the default rate until the user overrides it for their org.
  const DEFAULT_COST_PER_M2 = 65;
  const [costPerM2, setCostPerM2] = useState(DEFAULT_COST_PER_M2);

  useEffect(() => {
    if (!selectedOrgId) return;
    const saved = localStorage.getItem(`lakeCostPerM2:${selectedOrgId}`);
    const parsed = saved != null ? Number(saved) : NaN;
    setCostPerM2(Number.isFinite(parsed) ? parsed : DEFAULT_COST_PER_M2);
  }, [selectedOrgId]);

  const handleCostChange = (value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) && num >= 0 ? num : 0;
    setCostPerM2(safe);
    if (selectedOrgId) localStorage.setItem(`lakeCostPerM2:${selectedOrgId}`, String(safe));
  };

  useEffect(() => {
    if (orgLoading || !selectedOrgId) {
      if (!orgLoading) setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      try {
        const user = getAuth().currentUser;
        const token = user && await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch all data in parallel
        const [infestationRes, statsRes, areaRes, thresholdRes, expensesRes] = await Promise.all([
          fetch(`${BASE_URL}/api/lake/infestation?organisation_id=${selectedOrgId}`, { headers }),
          fetch(`${BASE_URL}/api/lake/stats-summary?organisation_id=${selectedOrgId}`, { headers }),
          fetch(`${BASE_URL}/api/lake/infestation-area?organisation_id=${selectedOrgId}`, { headers }),
          fetch(`${BASE_URL}/api/lake/threshold?organisation_id=${selectedOrgId}`, { headers }),
          fetch(`${BASE_URL}/api/lake/expenses-summary?organisation_id=${selectedOrgId}`, { headers })
        ]);

        const infestationData = await infestationRes.json();
        const statsData = await statsRes.json();
        const areaData = await areaRes.json();
        const thresholdData = await thresholdRes.json();
        const expensesData = await expensesRes.json();

        // Transform infestation data
        const formattedInfestation = infestationData.map(item => ({
          ...item,
          name: item.name.split("_").slice(0, 2).join(" "),
          infestation: Number(item.infestation),
          maintenance: Number(item.maintenance)
        }));

        // Calculate total expenses
        const totalExp = expensesData.reduce((acc, d) => acc + (Number(d.expense) || 0), 0);

        // Format expense data
        const colorPalette = [
          theme.palette.grey[600],
          theme.palette.success.light,
          theme.palette.error.light,
          theme.palette.success.main,
          theme.palette.info.main,
          theme.palette.error.main
        ];

        const formattedExpenses = expensesData.map((item, idx) => {
          const rawExpense = Number(item.expense) || 0;
          const percentage = totalExp > 0 ? Math.round((rawExpense / totalExp) * 100) : 0;

          return {
            id: `0${idx + 1}`,
            name: item.name,
            expense: rawExpense,
            percentage,
            color: colorPalette[idx % colorPalette.length],
            site_id: item.site_id
          };
        });

        setDashboardData({
          stats: statsData,
          infestationData: formattedInfestation,
          areaComparison: {
            infested: areaData.map(d => parseFloat(d.infested_area)),
            total: areaData.map(d => parseFloat(d.total_area)),
            lakeNames: areaData.map(d => d.name.split('_').slice(0, 2).join(' '))
          },
          thresholdBreaches: thresholdData.map(item => ({
            name: item.name.split('_').slice(0, 2).join(' '),
            count: Number(item.count) || 0,
            expenses: Number(item.expenses) || 0,
            site_id: item.site_id
          })),
          expenseData: formattedExpenses,
          totalExpense: totalExp,
          _areaRaw: areaData
        });
      } catch (error) {
        console.error('Error fetching lake dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [selectedOrgId]);

  const infestedM2BySite = useMemo(() => {
    const map = {};
    (dashboardData._areaRaw || []).forEach((d) => {
      map[d.site_id] = (Number(d.infested_area) || 0) * 4046.8564224;
    });
    return map;
  }, [dashboardData._areaRaw]);

  const infestationChartData = useMemo(() =>
    (dashboardData.infestationData || []).map((d) => ({
      ...d,
      maintenance: costPerM2 * (infestedM2BySite[d.site_id] || 0),
    })), [dashboardData.infestationData, infestedM2BySite, costPerM2]);

  const thresholdChartData = useMemo(() =>
    (dashboardData.thresholdBreaches || []).map((d) => ({
      ...d,
      expenses: costPerM2 * (infestedM2BySite[d.site_id] || 0),
    })), [dashboardData.thresholdBreaches, infestedM2BySite, costPerM2]);

  const expenseRows = useMemo(() => {
    const rows = (dashboardData.expenseData || []).map((d) => ({
      ...d,
      expense: costPerM2 * (infestedM2BySite[d.site_id] || 0),
    }));
    const total = rows.reduce((acc, r) => acc + r.expense, 0);
    return rows.map((r) => ({ ...r, percentage: total > 0 ? Math.round((r.expense / total) * 100) : 0 }));
  }, [dashboardData.expenseData, infestedM2BySite, costPerM2]);

  const totalExpense = useMemo(
    () => expenseRows.reduce((acc, r) => acc + r.expense, 0),
    [expenseRows]
  );

  // Custom Tooltip for Infestation Chart
  const InfestationTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, boxShadow: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.dataKey === 'maintenance' 
                ? `${(entry.value / 100000).toFixed(2)} L` 
                : `${entry.value}%`}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (loading) return <FullPageLoader />;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Lake Management Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Monitor lake health, infestation levels, and maintenance costs
          </Typography>
        </Box>
        <TextField
          size="small"
          type="number"
          label="Maintenance cost per m²"
          value={costPerM2}
          onChange={(e) => handleCostChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          sx={{ width: 220 }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Lakes"
            value={dashboardData.stats.total_lakes}
            subtitle={`Total Area: ${Math.round(dashboardData.stats.total_area || 0).toLocaleString()} acres`}
            icon={<Droplets size={24} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Infested Lakes"
            value={dashboardData.stats.infested_lakes}
            subtitle={`Infested Area: ${Math.round(dashboardData.stats.infested_area || 0).toLocaleString()} acres`}
            icon={<AlertTriangle size={24} />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Lakes at Risk"
            value={dashboardData.stats.lakes_at_risk}
            subtitle={`Risk Area: ${Math.round(dashboardData.stats.risk_area || 0).toLocaleString()} acres`}
            icon={<Coins size={24} />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Infestation Rate"
            value={`${dashboardData.stats.total_lakes ? Math.round((dashboardData.stats.infested_lakes / dashboardData.stats.total_lakes) * 100) : 0}%`}
            subtitle={`${dashboardData.stats.infested_lakes}/${dashboardData.stats.total_lakes} lakes infested`}
            icon={<TrendingUp size={24} />}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Charts Grid - 2 Columns */}
      <Grid container spacing={3}>
        {/* Lake Infestation Map */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Lake Infestation Map
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Lake sites colored by infestation level (grey = no data in last 3 months)
            </Typography>
            <LakeInfestationMap organisationId={selectedOrgId} />
          </Paper>
        </Grid>

        {/* Chart 1: Infestation Level vs. Threshold */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Infestation Level vs. Threshold
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Lake infestation levels and expected maintenance costs
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart
                data={infestationChartData}
                margin={{ top: 20, right: 30, bottom: 80, left: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="name"
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  tick={{ fontSize: 11 }}
                  height={60}
                />
                <YAxis 
                  yAxisId="left" 
                  domain={[0, 100]}
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Infestation %', angle: -90, position: 'insideLeft', dy: 50 }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(val) => `${(val / 100000).toFixed(1)}L`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<InfestationTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <ReferenceLine
                  y={60}
                  yAxisId="left"
                  stroke={theme.palette.error.main}
                  strokeDasharray="4 4"
                  label={{ value: 'Threshold', fill: theme.palette.error.main, fontSize: 11 }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="infestation"
                  barSize={20}
                  fill={theme.palette.success.main}
                  name="Infestation Level %"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="maintenance"
                  stroke={theme.palette.error.dark}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: theme.palette.error.dark }}
                  name="Maintenance Cost"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 2: Total Area vs Infested Area */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Total Area vs Infested Area
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Stacked comparison of lake areas in acres
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={dashboardData.areaComparison.lakeNames.map((name, idx) => ({
                  name,
                  infested: dashboardData.areaComparison.infested[idx] || 0,
                  total: dashboardData.areaComparison.total[idx] || 0
                }))}
                margin={{ top: 20, right: 30, bottom: 80, left: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="name"
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  tick={{ fontSize: 11 }}
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Area (acres)', angle: -90, position: 'insideLeft', dy: 50 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '6px'
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar
                  dataKey="infested"
                  stackId="a"
                  fill={theme.palette.error.main}
                  name="Infested Area (acres)"
                />
                <Bar
                  dataKey="total"
                  stackId="a"
                  fill={theme.palette.success.main}
                  name="Lake Total Area (acres)"
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 3: Threshold Breaches vs. Maintenance */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'error.main' }}>
              Threshold Breaches vs. Maintenance
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Breach count and maintenance expenses till date
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart
                data={thresholdChartData}
                margin={{ top: 20, right: 30, bottom: 80, left: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="name"
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  tick={{ fontSize: 11 }}
                  height={60}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Breach Count', angle: -90, position: 'insideLeft', dy: 50 }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(val) => `${(val / 100000).toFixed(1)}L`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '6px'
                  }}
                  formatter={(value, name) => {
                    if (name === 'Maintenance Expenses') {
                      return `${(value / 100000).toFixed(2)}L`;
                    }
                    return value;
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar
                  yAxisId="left"
                  dataKey="count"
                  fill={theme.palette.primary.main}
                  name="Threshold Breaches"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="expenses"
                  stroke={theme.palette.error.dark}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: theme.palette.error.dark }}
                  name="Maintenance Expenses"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 4: Total Expenses (Till Date) */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Total Expenses (Till Date): {Math.round(totalExpense).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Expense breakdown by category
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              height: 350,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '40px 1fr 120px 180px 80px',
                gap: 1,
                p: 1.5,
                borderBottom: `2px solid ${theme.palette.grey[200]}`,
                backgroundColor: theme.palette.grey[50],
                fontWeight: 600,
                fontSize: '11px',
                color: theme.palette.text.secondary,
                textTransform: 'uppercase'
              }}>
                <Box>#</Box>
                <Box>Name</Box>
                <Box>Expense</Box>
                <Box>% of Total Expense</Box>
                <Box></Box>
              </Box>

              {/* Table Body - Scrollable */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[100],
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: '3px',
                }
              }}>
                {expenseRows.map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 120px 180px 80px',
                      gap: 1,
                      p: 1.5,
                      borderBottom: `1px solid ${theme.palette.grey[100]}`,
                      fontSize: '13px',
                      '&:hover': {
                        backgroundColor: theme.palette.grey[50]
                      }
                    }}
                  >
                    <Box sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                      {row.id}
                    </Box>
                    <Box sx={{ fontWeight: 500 }}>
                      {row.name}
                    </Box>
                    <Box sx={{ fontWeight: 600 }}>
                      {Math.round(row.expense).toLocaleString()}
                    </Box>
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1 
                      }}>
                        <Box sx={{ 
                          flex: 1, 
                          height: 8, 
                          backgroundColor: theme.palette.grey[200], 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${row.percentage}%`,
                            height: '100%',
                            backgroundColor: row.color,
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                        <Box sx={{ 
                          minWidth: 35, 
                          fontSize: '12px', 
                          fontWeight: 600,
                          color: theme.palette.text.secondary
                        }}>
                          {row.percentage}%
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: row.color, 
                      borderRadius: '50%',
                      ml: 'auto'
                    }} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LakeDashboardModern;
